import { useState, useEffect, useRef } from 'react';
import maintenanceSalaryCombinationV2Service from '../services/maintenanceSalaryCombinationV2';
import { useEnumsApi } from './useEnumsApi';
import { FEATURES } from '../../config/features';
import { FILTER_CONFIG, PAYLOAD_TYPES } from '../components/filters/FilterConfig';
import { getDateNow } from '../../utils/textUtils';

export const useSalaryRateMatcher = ({
  formState,
  salaryType,
  activeEmployeeTypes,
  keyMap,
  effectiveDate = null
}) => {
  const [matchedRules, setMatchedRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const { maintenanceTypes } = useEnumsApi();
  const lastPayloadRef = useRef("");

  useEffect(() => {
    const fetchMatches = async () => {
      // 1. Guard: Wrong Salary Type
      if (salaryType !== "MaintenanceSalaryCombination") {
        if (matchedRules.length > 0) setMatchedRules([]);
        return;
      }

      // 2. Guard: Missing Data
      if (!maintenanceTypes?.length || !formState || !activeEmployeeTypes?.length) return;

      const empTypeStateKey = keyMap["EMPLOYEE_TYPE"];
      const selectedEmpTypeId = formState[empTypeStateKey];

      const selectedTypeObj = activeEmployeeTypes.find(
        t => t.id.toString() === selectedEmpTypeId?.toString()
      );

      const disregardOthers = selectedTypeObj?.maintenanceSalaryCombinationDisregardOtherFilter === true;
      const apiPayload = [];

      FEATURES.MAINTENANCE_SALARY_FILTERS.forEach((filterKey) => {
        const config = FILTER_CONFIG[filterKey];
        
        // Skip additional payload types (like EFFECTIVE_DATE_MSC_HISTORY_DROPDOWN)
        if (config?.payloadType === PAYLOAD_TYPES.ADDITIONAL) return;

        const dropdownKey = config?.dropdownTypeKey ?? filterKey;

        if (disregardOthers && dropdownKey !== "EMPLOYEE_TYPE") return;

        const stateKey = keyMap[dropdownKey] || keyMap[filterKey];
        const formValue = formState[stateKey];
        const typeInfo = maintenanceTypes.find(t => t.name === dropdownKey);

        if (formValue && typeInfo) {
          apiPayload.push({ type: typeInfo.value, value: parseInt(formValue) });
        }
      });

      // FIX: Only count filters whose payloadType is NOT 'additional'
      const actualMaintenanceFilters = FEATURES.MAINTENANCE_SALARY_FILTERS.filter(key => {
        return FILTER_CONFIG[key]?.payloadType !== PAYLOAD_TYPES.ADDITIONAL;
      });

      const expectedCount = disregardOthers ? 1 : actualMaintenanceFilters.length;
      const empTypeInfo = maintenanceTypes.find(t => t.name === "EMPLOYEE_TYPE");
      const hasEmployeeType = apiPayload.some(p => p.type === empTypeInfo?.value);

      const currentPayloadString = JSON.stringify(apiPayload);

      // 3. Guard: Validation & Duplicate Prevention
      if (apiPayload.length === expectedCount && hasEmployeeType) {
        if (currentPayloadString === lastPayloadRef.current) return;

        lastPayloadRef.current = currentPayloadString;
        setLoading(true);

        try {
          const additionalFilters = {
            effectiveDate: formState?.effectiveDate || effectiveDate || getDateNow()
          };
          const res = await maintenanceSalaryCombinationV2Service.getDashboard(apiPayload, additionalFilters);
          setMatchedRules(res.data.rules || []);
        } catch (err) {
          setMatchedRules([]);
          console.error("Fetch Error: ", err);
        } finally {
          setLoading(false);
        }
      } else {
        if (matchedRules.length > 0) setMatchedRules([]);
        lastPayloadRef.current = "";
      }
    };

    fetchMatches();
  }, [
    JSON.stringify(formState),
    salaryType,
    maintenanceTypes,
    activeEmployeeTypes,
    keyMap,
    effectiveDate
  ]);

  return { matchedRules, loading };
};